import * as _ from "lodash";
import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import {
    ApolloClient,
    InMemoryCache,
    HttpLink,
    ApolloProvider,
    from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { get, post } from "../services/HttpService";
import { proxies } from "../services/RepositoryService";

const headers = {
    "Content-Type": "application/json",
};

const StarekContext = React.createContext({
    snackbarNotification: null,
    auth: null,

    lastUsedWorkspace: null,
    lastUsedPlant: null,

    engineClient: null,

    setSnackbarNotification: () => {
        console.log("provide implementation");
    },
    showError: () => {
        console.log("provide implementation");
    },
    showWarning: () => {
        console.log("provide implementation");
    },
    showInfo: () => {
        console.log("provide implementation");
    },
    logout: () => {
        console.log("provide implementation");
    },
    login: (workspace, username, password) => {
        console.log("provide implementation");
    },
    ssoLogin: (parameters) => {
        console.log("provide implementation");
    },
});

const createEngineClient = (workspace, authToken) => {
    if (!workspace) return null;
    const uri = `${workspace.engine.scheme || "http"}://${
        workspace.engine.host
    }:${workspace.engine.port}/api/v1/${workspace.workspace_id}/graphql`;
    const httpLink = new HttpLink({ uri });
    const authLink = setContext((_, { headers }) => {
        return {
            headers: {
                ...headers,
                Authorization: authToken ? `Bearer ${authToken}` : "",
            },
        };
    });

    return new ApolloClient({
        link: from([authLink, httpLink]),
        cache: new InMemoryCache(),
    });
};

export const StarekContextProvider = (props) => {
    const [snackbarNotification, setSnackbarNotification] = useState(null);
    const [authToken, setAuthToken] = useState(Cookies.get("token"));
    const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
    const [lastUsedWorkspace, setLastUsedWorkspace] = useState(
        JSON.parse(localStorage.getItem("lastUsedWorkspace"))
    );
    const [lastUsedPlant, setLastUsedPlant] = useState(
        JSON.parse(localStorage.getItem("lastUsedPlant"))
    );
    const [availablePlants, setAvailablePlants] = useState([]);

    const [engineClient, setEngineClient] = useState(
        createEngineClient(lastUsedWorkspace, authToken)
    );
    const [types, setTypes] = useState(null);

    useEffect(() => {
        if (!user) return;
        localStorage.setItem("user", JSON.stringify(user));
    }, [user]);

    useEffect(() => {
        if (!authToken) {
            Cookies.remove("token");
            return;
        }
        Cookies.set("token", authToken);
        const interval = setInterval(() => {
            const cookieAuth = Cookies.get("token");
            if (cookieAuth !== authToken) {
                setAuthToken(cookieAuth);
            }
        }, 60000);
        return () => clearInterval(interval);
    }, [authToken]);

    useEffect(() => {
        if (!lastUsedPlant) return;
        localStorage.setItem("lastUsedPlant", JSON.stringify(lastUsedPlant));
    }, [lastUsedPlant]);

    useEffect(() => {
        if (!lastUsedWorkspace) return;

        const fetchData = async () => {
            localStorage.setItem(
                "lastUsedWorkspace",
                JSON.stringify(lastUsedWorkspace)
            );
            setEngineClient(createEngineClient(lastUsedWorkspace, authToken));
            const response = await fetchTypes(lastUsedWorkspace);
            if (response) {
                setTypes(response);
            }
        };

        fetchData();
    }, [lastUsedWorkspace, authToken, lastUsedPlant]);

    const showError = async (error) => {
        await setSnackbarNotification({
            type: "error",
            text: error || "There was an error processing this request",
        });
    };

    const showWarning = async (warning) => {
        await setSnackbarNotification({
            type: "warning",
            text: warning || "There was a warning processing this request",
        });
    };

    const showInfo = async (message) => {
        await setSnackbarNotification({
            type: "info",
            text: message || "Here is some information",
        });
    };

    const login = async (workspace, username, password) => {
        const foundWorkspace = await proxies(workspace);
        setLastUsedWorkspace(foundWorkspace);

        const url = `${foundWorkspace.engine.scheme}://${foundWorkspace.engine.host}:${foundWorkspace.engine.port}${process.env.REACT_APP_API_PREFIX}/authenticate`;
        const body = {
            workspace: foundWorkspace.workspace_id,
            username: username,
            password: password,
        };
        const auth = await post(url, body, headers);

        setUser(auth.user);
        setAuthToken(auth.token);
        return auth;
    };

    const ssoLogin = async (parameters) => {
        console.log("SSO login called with", parameters);
        const { code, workspace, returnUriHost } = parameters;
        const foundWorkspace = await proxies(workspace);
        setLastUsedWorkspace(foundWorkspace);
        const url = `${foundWorkspace.engine.scheme}://${foundWorkspace.engine.host}:${foundWorkspace.engine.port}${process.env.REACT_APP_API_PREFIX}/sso-authenticate`;
        const body = {
            code,
            workspace: foundWorkspace.workspace_id,
            returnUriHost,
        };
        const auth = await post(url, body, headers);
        setUser(auth.user);
        setAuthToken(auth.token);
        return auth;
    };

    const fetchTypes = async (workspace) => {
        const url = `${workspace.engine.scheme}://${workspace.engine.host}:${workspace.engine.port}/api/v1/_types`;
        try {
            const response = await post(url, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            return response;
        } catch (error) {
            await showError(error.message || "Failed to fetch types");
            throw error;
        }
    };

    const logout = async () => {
        setAuthToken(null);
    };

    return (
        <StarekContext.Provider
            value={{
                snackbarNotification,
                auth: authToken,
                lastUsedWorkspace,
                lastUsedPlant,
                availablePlants,
                user,
                setSnackbarNotification,
                showError,
                showWarning,
                showInfo,
                setAuth: setAuthToken,
                setLastUsedWorkspace,
                setLastUsedPlant,
                setAvailablePlants,
                logout,
                login,
                ssoLogin,
                types,
            }}
        >
            {engineClient ? (
                <ApolloProvider client={engineClient}>
                    {props.children}
                </ApolloProvider>
            ) : (
                <>{props.children}</>
            )}
        </StarekContext.Provider>
    );
};

export default StarekContext;
